import React, { useEffect, useState, ReactNode, createContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import i18n from '../i18n/config'
import type { I18nContextType } from '../types'
import { getDefaultLanguageFromDomain, getDefaultCountryFromDomain } from '../utils/domain'

// eslint-disable-next-line react-refresh/only-export-components
export const I18nContext = createContext<I18nContextType | undefined>(undefined)

interface I18nProviderProps {
    children: ReactNode
}

const COUNTRY_CONFIG = {
    US: {
        language: 'en',
        currency: 'USD',
        currencySymbol: '$',
    },
    NL: {
        language: 'nl',
        currency: 'EUR',
        currencySymbol: '€',
    },
    BE: {
        language: 'nl',
        currency: 'EUR',
        currencySymbol: '€',
    },
} as const

// Country code to country mapping
const COUNTRY_CODE_MAP: Record<string, string> = {
    'nl': 'NL',
    'be': 'BE',
    'us': 'US'
}

// Country to default language mapping
const COUNTRY_LANGUAGE_MAP: Record<string, string> = {
    'NL': 'nl',
    'BE': 'nl', // Belgium uses Dutch
    'US': 'en'
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
    const location = useLocation()
    const navigate = useNavigate()

    const [language, setLanguageState] = useState(() => {
        // Check if language is already stored in localStorage
        const storedLanguage = localStorage.getItem('language')
        if (storedLanguage) {
            return storedLanguage
        }

        // If no stored language, use domain-based default
        return getDefaultLanguageFromDomain()
    })

    const [country, setCountryState] = useState(() => {
        // Check if country is already stored in localStorage
        const storedCountry = localStorage.getItem('country')
        if (storedCountry) {
            return storedCountry
        }

        // If no stored country, use domain-based default
        return getDefaultCountryFromDomain()
    })

    const currency = COUNTRY_CONFIG[country as keyof typeof COUNTRY_CONFIG]?.currency || 'USD'

    // Extract country code from URL if present
    const extractCountryFromUrl = (pathname: string): string | null => {
        const match = pathname.match(/^\/([a-z]{2})\//i)
        if (match) {
            const countryCode = match[1].toLowerCase()
            return COUNTRY_CODE_MAP[countryCode] || null
        }
        return null
    }



    // Handle country switching with redirect logic
    const handleCountryChange = (newCountry: string, isManualSwitch: boolean = false) => {
        const currentPath = location.pathname
        const urlCountry = extractCountryFromUrl(currentPath)

        // If this is a manual switch and we're on a country-specific URL
        if (isManualSwitch && urlCountry && urlCountry !== newCountry) {
            // Redirect to the same content in the new country
            const newPath = currentPath.replace(/^\/[a-z]{2}/i, `/${newCountry.toLowerCase()}`)
            navigate(newPath, { replace: true })
        }

        // Update country context and localStorage
        setCountryState(newCountry)
        localStorage.setItem('country', newCountry)

        // Set the default language for this country
        const language = COUNTRY_LANGUAGE_MAP[newCountry]
        if (language) {
            setLanguageState(language)
            localStorage.setItem('language', language)
            i18n.changeLanguage(language)
        }
    }

    // Effect to handle URL-based country changes
    useEffect(() => {
        const urlCountry = extractCountryFromUrl(location.pathname)

        if (urlCountry && urlCountry !== country) {
            // URL country differs from current country - update context
            setCountryState(urlCountry)
            localStorage.setItem('country', urlCountry)

            // Set the default language for this country
            const language = COUNTRY_LANGUAGE_MAP[urlCountry]
            if (language && language !== language) {
                setLanguageState(language)
                localStorage.setItem('language', language)
                i18n.changeLanguage(language)
            }
        }
    }, [location.pathname, country])

    useEffect(() => {
        i18n.changeLanguage(language)
    }, [language])

    const setLanguage = (lang: string) => {
        setLanguageState(lang)
        localStorage.setItem('language', lang)
        i18n.changeLanguage(lang)
    }

    const setCountry = (newCountry: string) => {
        handleCountryChange(newCountry, true) // true = manual switch
    }

    const t = (key: string, params?: Record<string, unknown>) => {
        return i18n.t(key, params)
    }

    const value: I18nContextType = {
        language,
        country,
        currency,
        setLanguage,
        setCountry,
        t,
    }

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    )
} 